import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Signup from "@/pages/Signup";
import ResetPassword from "@/pages/ResetPassword";

import OwnerLayout from "@/pages/OwnerLayout";
import Overview from "@/pages/owner/Overview";
import IVRBuilder from "@/pages/owner/IVRBuilder";
import Recordings from "@/pages/owner/Recordings";
import MapPage from "@/pages/owner/MapPage";
import Tasks from "@/pages/owner/Tasks";
import Healthcare from "@/pages/owner/Healthcare";
import Employees from "@/pages/owner/Employees";
import CallLogs from "@/pages/owner/CallLogs";
import Settings from "@/pages/owner/Settings";
import Attendance from "@/pages/owner/Attendance";
import Customers from "@/pages/owner/Customers";
import Admins from "@/pages/owner/Admins";
import ChatHub from "@/pages/ChatHub";
import Backup from "@/pages/owner/Backup";
import Marketplace from "@/pages/owner/Marketplace";
import Shipping from "@/pages/owner/Shipping";
import Orders from "@/pages/owner/Orders";
import Branding from "@/pages/owner/Branding";
import DomainGuide from "@/pages/owner/DomainGuide";
import EmployeeDeliveries from "@/pages/owner/EmployeeDeliveries";

import EmployeeLayout from "@/pages/EmployeeLayout";
import EmpHome from "@/pages/employee/EmpHome";
import EmpCustomers from "@/pages/employee/EmpCustomers";
import EmpTasks from "@/pages/employee/EmpTasks";
import EmpCalls from "@/pages/employee/EmpCalls";

import ShopSignup from "@/pages/shop/ShopSignup";
import ShopLogin from "@/pages/shop/ShopLogin";
import ShopAccount from "@/pages/shop/ShopAccount";
import ShopCatalog from "@/pages/shop/ShopCatalog";
import ShopCheckout from "@/pages/shop/ShopCheckout";
import ShopOrders from "@/pages/shop/ShopOrders";
import ShopTrack from "@/pages/shop/ShopTrack";
import ShopChat from "@/pages/shop/ShopChat";
import ShopPolicy from "@/pages/shop/ShopPolicy";
import { CartProvider } from "@/pages/shop/cartContext";
import { TenantProvider } from "@/pages/shop/tenantContext";
import { SupportFAB } from "@/pages/shop/SupportFAB";

import CareBooking from "@/pages/care/CareBooking";
import CareReport from "@/pages/care/CareReport";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute role="marketplace">
                  <OwnerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ProtectedRoute role="owner"><Overview /></ProtectedRoute>} />
              <Route path="ivr" element={<ProtectedRoute role="owner"><IVRBuilder /></ProtectedRoute>} />
              <Route path="recordings" element={<ProtectedRoute role="owner"><Recordings /></ProtectedRoute>} />
              <Route path="map" element={<ProtectedRoute role="owner"><MapPage /></ProtectedRoute>} />
              <Route path="tasks" element={<ProtectedRoute role="owner"><Tasks /></ProtectedRoute>} />
              <Route path="healthcare" element={<ProtectedRoute role="owner" needs="healthcare"><Healthcare /></ProtectedRoute>} />
              <Route path="employees" element={<ProtectedRoute role="owner"><Employees /></ProtectedRoute>} />
              <Route path="customers" element={<ProtectedRoute role="owner"><Customers /></ProtectedRoute>} />
              <Route path="attendance" element={<ProtectedRoute role="owner"><Attendance /></ProtectedRoute>} />
              <Route path="calls" element={<ProtectedRoute role="owner"><CallLogs /></ProtectedRoute>} />
              <Route path="chat" element={<ProtectedRoute role="owner"><ChatHub /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute role="owner"><Settings /></ProtectedRoute>} />
              <Route path="admins" element={<ProtectedRoute role="owner"><Admins /></ProtectedRoute>} />
              <Route path="backup" element={<ProtectedRoute role="owner"><Backup /></ProtectedRoute>} />
              <Route path="marketplace" element={<Marketplace />} />
              <Route path="shipping" element={<Shipping />} />
              <Route path="orders" element={<Orders />} />
              <Route path="branding" element={<ProtectedRoute role="owner"><Branding /></ProtectedRoute>} />
              <Route path="domain" element={<ProtectedRoute role="owner" needs="superAdmin"><DomainGuide /></ProtectedRoute>} />
              <Route path="deliveries" element={<ProtectedRoute role="owner"><EmployeeDeliveries /></ProtectedRoute>} />
            </Route>

            <Route
              path="/app"
              element={
                <ProtectedRoute role="employee">
                  <EmployeeLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<EmpHome />} />
              <Route path="customers" element={<EmpCustomers />} />
              <Route path="tasks" element={<EmpTasks />} />
              <Route path="calls" element={<EmpCalls />} />
              <Route path="chat" element={<ChatHub compact />} />
            </Route>

            {/* Customer storefront — public signup / login, browse & checkout.
                Everything under /shop shares the localStorage cart. Slugged
                URLs (/shop/:slug) resolve to the same catalog page but load
                the branded tenant. */}
            <Route
              path="/shop/*"
              element={
                <CartProvider>
                  <SupportFAB />
                  <Routes>
                    <Route path="signup" element={<TenantProvider><ShopSignup /></TenantProvider>} />
                    <Route path="login" element={<TenantProvider><ShopLogin /></TenantProvider>} />
                    <Route path="account" element={<TenantProvider><ShopAccount /></TenantProvider>} />
                    <Route path="orders" element={<TenantProvider><ShopOrders /></TenantProvider>} />
                    {/* Public order-tracking — no login required. The URL
                        carries a signed token in the `t` query param. */}
                    <Route path="track/:orderId" element={<ShopTrack />} />
                    <Route path="checkout" element={<TenantProvider><ShopCheckout /></TenantProvider>} />
                    <Route path="chat" element={<TenantProvider><ShopChat /></TenantProvider>} />
                    {/* Terms, refund, shipping, privacy, contact: payment gateways check these. */}
                    <Route path="policy/:kind" element={<TenantProvider><ShopPolicy /></TenantProvider>} />
                    <Route path=":slug/policy/:kind" element={<TenantProvider><ShopPolicy /></TenantProvider>} />
                    <Route index element={<TenantProvider><ShopCatalog /></TenantProvider>} />
                    <Route path=":slug" element={<TenantProvider><ShopCatalog /></TenantProvider>} />
                    <Route path="*" element={<Navigate to="/shop" replace />} />
                  </Routes>
                </CartProvider>
              }
            />

            {/* Nursing-home patient portal — doctor slot booking + pathology
                test booking, and the token-based report link patients get
                after booking a test. No login, same pattern as /shop. */}
            <Route
              path="/care/*"
              element={
                <Routes>
                  <Route path="report/:orderId" element={<CareReport />} />
                  <Route path=":slug" element={<TenantProvider><CareBooking /></TenantProvider>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
